import { observer } from "mobx-react-lite";
import { CheckCircleIcon, DatabaseIcon } from "lucide-react";

import { AssertValidConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const types = [
  { value: "object", label: "JSON" },
  { value: "number", label: "Number" },
  { value: "sql", label: "SQL" },
] as const;

const validationSchema = createBlockSchema<AssertValidConfiguration>()
  .requireName()
  .addRule(required("configuration.type", "Type is required"))
  .addRule(required("configuration.source", "Source is required"))
  .build();

const TypeValidationBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<AssertValidConfiguration>, "DefaultFields">) => {
    const block = useBlock<AssertValidConfiguration>();
    const isReadonly = useIsReadonly();

    const handleTypeChange = (type: "number" | "object" | "sql") => {
      block.setConfiguration({
        ...block.configuration,
        type,
        source: block.configuration?.source || "",
      });
    };

    return (
      <>
        <SectionGroup
          title="Validation Configuration"
          description="Configure the type of validation to perform"
          icon={<CheckCircleIcon className="h-4 w-4" />}
        >
          <ValidatedField name="Type" path="configuration.type">
            <Select
              value={block.configuration?.type || undefined}
              onValueChange={handleTypeChange}
              disabled={isReadonly}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a type..." />
              </SelectTrigger>
              <SelectContent>
                {types.map(typeOption => (
                  <SelectItem key={typeOption.value} value={typeOption.value}>
                    {typeOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ValidatedField>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the source to validate"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<AssertValidConfiguration>(TypeValidationBlockCore, {
  validationSchema,
});
